import type React from "react";

import type { ButtonBaseProps, ButtonButtonProps } from "@/design-system/Button";
import { Button } from "@/design-system/Button";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useConfig } from "@/providers/config/useConfig";
import { useTranslation } from "@/translations";

import type { AskNobiConfig } from "./AskNobiProvider";
import { useAskNobi } from "./AskNobiProvider";

type AskNobiButtonProps = Omit<ButtonBaseProps, "children"> &
  Omit<ButtonButtonProps, "onClick"> &
  AskNobiConfig & { children?: React.ReactNode };

/**
 * Drop-in button that opens the Nobi AI drawer when clicked.
 * Only renders for admins when the Nobi feature is available.
 * Pass any `AskNobiConfig` props to configure the drawer's suggestions,
 * context banner, filters, etc. Defaults to a generic "Ask Nobi" label.
 *
 * Keep access checks and drawer behaviour in this wrapper while the
 * shared AI button styling lives in the base Button variant. Keep feature
 * availability and drawer behaviour in this wrapper.
 */
export function AskNobiButton({
  suggestions,
  defaultFilters,
  context,
  initialMessage,
  children,
  ...buttonProps
}: AskNobiButtonProps): React.ReactNode {
  const { open } = useAskNobi();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const isNobiAvailable = useConfig("isNobiAvailable");

  if (!sessionUser.isAdmin || !isNobiAvailable) return null;

  return (
    <Button
      variant="ai"
      {...buttonProps}
      onClick={() => open({ suggestions, defaultFilters, context, initialMessage })}
    >
      {children ?? t("component.ask-nobi-button.label")}
    </Button>
  );
}
